/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * System Chapter Component (Layer 2)
 *
 * Displays detailed system-specific information as an interactive overlay.
 * Contains the same substance as the system pages in the full PDF,
 * but presented interactively with expandable sections.
 *
 * Sections:
 * - System layout diagram
 * - Behavioral archetype and description
 * - Comfort-over-time visualization
 * - Hot water capability and limitations
 * - "Works best when" guidance
 * - "Things to be aware of" caveats
 */

import { useState } from 'react';
import { useStoryContext } from '../context/StoryContext';
import { systemChapterContent, type SystemChapterData } from '../content/systemChapterContent';

interface SystemChapterProps {
  optionId: string;
}

interface ExpandableSectionProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

function ExpandableSection({ title, icon, children, defaultExpanded = false }: ExpandableSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className={`chapter-section ${isExpanded ? 'expanded' : ''}`}>
      <button
        className="chapter-section-header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="section-icon">{icon}</span>
        <span className="section-title">{title}</span>
        <span className="section-chevron">{isExpanded ? '▼' : '▶'}</span>
      </button>
      {isExpanded && (
        <div className="chapter-section-content">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Inline SVG icons for system diagrams (same as print)
 */
const SystemIcons = {
  Boiler: () => (
    <svg width="48" height="72" viewBox="0 0 40 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="system-icon-svg">
      <rect width="40" height="60" rx="4" fill="#E2E8F0" />
      <rect x="5" y="45" width="30" height="10" rx="2" fill="#94A3B8" />
      <circle cx="20" cy="15" r="5" stroke="#475569" strokeWidth="2" fill="none" />
      <rect x="15" y="25" width="10" height="15" rx="1" fill="#64748B" />
    </svg>
  ),
  Cylinder: () => (
    <svg width="48" height="84" viewBox="0 0 40 70" fill="none" xmlns="http://www.w3.org/2000/svg" className="system-icon-svg">
      <path d="M0 10C0 4.47715 4.47715 0 10 0H30C35.5228 0 40 4.47715 40 10V60C40 65.5228 35.5228 70 30 70H10C4.47715 70 0 65.5228 0 60V10Z" fill="#CBD5E1" />
      <rect x="0" y="20" width="40" height="2" fill="#94A3B8" />
      <rect x="0" y="50" width="40" height="2" fill="#94A3B8" />
      <circle cx="20" cy="35" r="8" fill="#E0E7EF" />
    </svg>
  ),
  HeatPump: () => (
    <svg width="48" height="72" viewBox="0 0 40 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="system-icon-svg">
      <rect width="40" height="60" rx="4" fill="#D1FAE5" />
      <path d="M12 20 L20 30 L12 40" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M20 20 L28 30 L20 40" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <rect x="8" y="48" width="24" height="8" rx="2" fill="#059669" />
    </svg>
  ),
  TankFE: () => (
    <svg width="36" height="48" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="system-icon-svg">
      <rect x="3" y="10" width="24" height="26" rx="2" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="1.5" />
      <ellipse cx="15" cy="10" rx="12" ry="4" fill="#BFDBFE" />
      <text x="15" y="27" textAnchor="middle" fontSize="7" fill="#1e40af" fontWeight="600">F&E</text>
    </svg>
  ),
  TankCWS: () => (
    <svg width="60" height="72" viewBox="0 0 50 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="system-icon-svg">
      <rect x="5" y="12" width="40" height="42" rx="3" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="2" />
      <ellipse cx="25" cy="12" rx="20" ry="6" fill="#BFDBFE" />
      <rect x="12" y="22" width="26" height="2" fill="#60A5FA" />
      <rect x="12" y="32" width="26" height="2" fill="#60A5FA" />
      <rect x="12" y="42" width="26" height="2" fill="#60A5FA" />
      <text x="25" y="56" textAnchor="middle" fontSize="8" fill="#1e40af" fontWeight="600">CWS</text>
    </svg>
  ),
};

/**
 * System layout diagram component
 */
function SystemLayoutDiagram({ layout }: { layout: SystemChapterData['layout'] }) {
  const renderIcon = (iconKey: string) => {
    switch (iconKey) {
      case 'boiler': return <SystemIcons.Boiler />;
      case 'cylinder': return <SystemIcons.Cylinder />;
      case 'heat_pump': return <SystemIcons.HeatPump />;
      case 'tank_fe': return <SystemIcons.TankFE />;
      case 'tank_cws': return <SystemIcons.TankCWS />;
      default: return <SystemIcons.Boiler />;
    }
  };

  return (
    <div className="system-layout-diagram">
      <div className="layout-title">{layout.title}</div>
      <div className="layout-components">
        {layout.components.map((component, idx) => (
          <div key={idx} className="layout-component">
            <div className="component-icon">
              {renderIcon(component.icon)}
            </div>
            <div className="component-label">{component.label}</div>
          </div>
        ))}
      </div>
      <div className="layout-note">{layout.tankNote}</div>
    </div>
  );
}

/**
 * Interactive comfort chart (simplified SVG for story)
 */
function ComfortVisualization({ comfort }: { comfort: SystemChapterData['comfort'] }) {
  const W = 400;
  const H = 160;
  const PAD = { top: 20, right: 12, bottom: 28, left: 36 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const xScale = (hour: number) => PAD.left + (hour / 24) * plotW;
  const yScale = (val: number) => PAD.top + plotH - (val / 100) * plotH;

  const pathPoints = comfort.curve.map(
    (p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.hour).toFixed(1)} ${yScale(p.comfort).toFixed(1)}`
  );
  const pathD = pathPoints.join(' ');

  return (
    <div className="comfort-visualization">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" className="comfort-chart-svg">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map((tick) => (
          <line
            key={`gy-${tick}`}
            x1={PAD.left}
            y1={yScale(tick)}
            x2={W - PAD.right}
            y2={yScale(tick)}
            stroke="#e5e7eb"
            strokeWidth="0.5"
          />
        ))}

        {/* Time markers */}
        {[8, 18].map((h) => (
          <line
            key={`marker-${h}`}
            x1={xScale(h)}
            y1={PAD.top}
            x2={xScale(h)}
            y2={PAD.top + plotH}
            stroke="#9ca3af"
            strokeWidth="1"
            strokeDasharray="4 3"
          />
        ))}

        {/* Comfort curve */}
        <path
          d={pathD}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* X-axis labels */}
        {[0, 6, 12, 18, 24].map((h) => (
          <text
            key={`xt-${h}`}
            x={xScale(h)}
            y={PAD.top + plotH + 16}
            textAnchor="middle"
            fontSize="10"
            fill="#6b7280"
          >
            {h}h
          </text>
        ))}

        {/* Y-axis label */}
        <text
          x={8}
          y={PAD.top + plotH / 2}
          textAnchor="middle"
          fontSize="9"
          fill="#9ca3af"
          transform={`rotate(-90, 8, ${PAD.top + plotH / 2})`}
        >
          Comfort
        </text>
      </svg>
      <ul className="comfort-bullets">
        {comfort.bullets.map((bullet, i) => (
          <li key={i}>{bullet}</li>
        ))}
      </ul>
    </div>
  );
}

export function SystemChapter({ optionId }: SystemChapterProps) {
  const { closeOverlay } = useStoryContext();
  const chapter = systemChapterContent[optionId];

  if (!chapter) {
    return (
      <div className="system-chapter-overlay">
        <div className="chapter-container">
          <div className="chapter-header">
            <h2>System Details</h2>
            <button className="chapter-close" onClick={closeOverlay} aria-label="Close">
              ✕
            </button>
          </div>
          <div className="chapter-body">
            <p>Detailed information for this system is not yet available.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="system-chapter-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) closeOverlay();
    }}>
      <div className="chapter-container">
        <div className="chapter-header">
          <div className="header-content">
            <span className="archetype-icon">{chapter.archetype.icon}</span>
            <div>
              <h2 className="chapter-title">{chapter.title}</h2>
              <p className="archetype-label">{chapter.archetype.label}</p>
            </div>
          </div>
          <button className="chapter-close" onClick={closeOverlay} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="chapter-body">
          <p className="chapter-intro">{chapter.archetype.summary}</p>

          <ExpandableSection title="System Layout" icon="🏠" defaultExpanded={true}>
            <SystemLayoutDiagram layout={chapter.layout} />
          </ExpandableSection>

          <ExpandableSection title="Comfort Over Time" icon="📊" defaultExpanded={true}>
            <ComfortVisualization comfort={chapter.comfort} />
          </ExpandableSection>

          <ExpandableSection title="Hot Water Capability" icon="🚿" defaultExpanded={true}>
            <div className="hot-water-section">
              <div className="capability-summary">{chapter.hotWater.summary}</div>
              <ul className="capability-details">
                {chapter.hotWater.details.map((detail, i) => (
                  <li key={i}>{detail}</li>
                ))}
              </ul>
              {chapter.hotWater.limitations.length > 0 && (
                <div className="limitations">
                  <h4>Limitations</h4>
                  <ul>
                    {chapter.hotWater.limitations.map((limit, i) => (
                      <li key={i}>{limit}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </ExpandableSection>

          <ExpandableSection title="Works Best When" icon="✓">
            <div className="works-best-section">
              <ul>
                {chapter.worksBestWhen.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </ExpandableSection>

          <ExpandableSection title="Things to Be Aware Of" icon="⚠">
            <div className="caveats-section">
              <ul>
                {chapter.caveats.map((caveat, i) => (
                  <li key={i}>{caveat}</li>
                ))}
              </ul>
            </div>
          </ExpandableSection>
        </div>

        <div className="chapter-footer">
          <button className="return-button" onClick={closeOverlay}>
            ← Return to story
          </button>
        </div>
      </div>
    </div>
  );
}

export default SystemChapter;
